
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import WhyChooseUsSection from './components/WhyChooseUsSection';
import StrategySection from './components/StrategySection';
import PassionSection from './components/PassionSection';
import ServicesSection from './components/ServicesSection';
import ContactSection from './components/ContactSection';

export default function Home() {
  useEffect(() => {
    // Schema.org JSON-LD optimisé pour le SEO Belgique
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "FMS Protec",
      "alternateName": "FMS Protec Charleroi",
      "description": "Entreprise sécurité Belgique spécialisée dans l'installation d'alarme maison, caméra de surveillance, contrôle d'accès et détecteur incendie. Service particuliers et PME.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "telephone": "+32484660814",
      "email": "fms.protec@gmail.com",
      "founder": {
        "@type": "Person",
        "name": "Maxime Fragapane"
      },
      "serviceType": [
        "Alarme maison Belgique",
        "Caméra de surveillance Belgique", 
        "Contrôle d'accès Belgique",
        "Détecteur incendie Belgique",
        "Sécurité PME Belgique",
        "Installation système anti-intrusion Belgique"
      ],
      "areaServed": [
        {
          "@type": "City",
          "name": "Charleroi",
          "addressCountry": "BE"
        },
        {
          "@type": "City", 
          "name": "Bruxelles",
          "addressCountry": "BE"
        },
        {
          "@type": "City",
          "name": "Namur", 
          "addressCountry": "BE"
        },
        {
          "@type": "City",
          "name": "Liège",
          "addressCountry": "BE"
        },
        {
          "@type": "City",
          "name": "Mons",
          "addressCountry": "BE"
        },
        {
          "@type": "Country",
          "name": "Belgium"
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Charleroi",
        "addressRegion": "Hainaut",
        "addressCountry": "BE",
        "postalCode": "6000"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "50.4108",
        "longitude": "4.4446"
      },
      "priceRange": "$$",
      "slogan": "LA SÉRÉNITÉ AU-DEHORS ET LA SÉCURITÉ AU DEDANS",
      "keywords": "entreprise sécurité Belgique, alarme maison Belgique, caméra de surveillance Belgique, contrôle d'accès Belgique, sécurité Charleroi",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Services Sécurité Électronique",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Alarme maison Belgique",
              "description": "Installation système alarme maison en Belgique"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Caméra de surveillance Belgique",
              "description": "Installation caméra surveillance professionnelle en Belgique"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Contrôle d'accès Belgique",
              "description": "Installation système contrôle accès en Belgique"
            }
          }
        ]
      }
    });
    
    // Schema Organization optimisé
    const orgSchemaScript = document.createElement('script');
    orgSchemaScript.type = 'application/ld+json';
    orgSchemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "FMS Protec",
      "alternateName": "FMS Protec Belgique",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "logo": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/vite.svg`,
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+32484660814",
          "email": "fms.protec@gmail.com",
          "contactType": "customer service",
          "availableLanguage": ["French"],
          "areaServed": "BE",
          "serviceType": "Sécurité électronique"
        }
      ],
      "founder": {
        "@type": "Person",
        "name": "Maxime Fragapane"
      },
      "foundingDate": "2024",
      "description": "Entreprise sécurité Belgique spécialisée alarme maison, caméra surveillance, contrôle accès",
      "keywords": "entreprise sécurité Belgique, alarme maison Belgique, sécurité Charleroi",
      "sameAs": [
        `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
      ]
    });

    // Schema WebSite avec SearchAction
    const websiteSchemaScript = document.createElement('script');
    websiteSchemaScript.type = 'application/ld+json';
    websiteSchemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "FMS Protec - Entreprise Sécurité Belgique",
      "alternateName": "FMS Protec Charleroi",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "description": "Entreprise sécurité Belgique : installation alarme maison, caméra surveillance, contrôle accès. Service Charleroi, Bruxelles, Namur, Liège, Mons.",
      "publisher": {
        "@type": "Organization",
        "name": "FMS Protec"
      },
      "inLanguage": "fr-BE",
      "keywords": "entreprise sécurité Belgique, alarme maison Belgique, caméra de surveillance Belgique, contrôle d'accès Belgique, sécurité Charleroi, sécurité PME Belgique"
    });

    // Schema BreadcrumbList pour la navigation
    const breadcrumbSchemaScript = document.createElement('script');
    breadcrumbSchemaScript.type = 'application/ld+json';
    breadcrumbSchemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": "Services Sécurité Belgique",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}#services`
        },
        {
          "@type": "ListItem",
          "position": 3, 
          "name": "Contact Devis Gratuit",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}#contact`
        }
      ]
    });

    document.head.appendChild(schemaScript);
    document.head.appendChild(orgSchemaScript);
    document.head.appendChild(websiteSchemaScript);
    document.head.appendChild(breadcrumbSchemaScript);

    return () => {
      if (document.head.contains(schemaScript)) document.head.removeChild(schemaScript);
      if (document.head.contains(orgSchemaScript)) document.head.removeChild(orgSchemaScript);
      if (document.head.contains(websiteSchemaScript)) document.head.removeChild(websiteSchemaScript);
      if (document.head.contains(breadcrumbSchemaScript)) document.head.removeChild(breadcrumbSchemaScript);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <HeroSection />
      <WhyChooseUsSection />
      <StrategySection />
      <PassionSection />
      <ServicesSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
